import React, { useEffect, useState } from 'react';
import { Link2, Copy, Check, ExternalLink, CheckCircle2, PencilLine, Smartphone } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function SmsAlertConfig() {
  const token = sessionStorage.getItem('admin_token');
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    fetch(`${API_URL}/api/admin/alert-phone`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((d) => setPhone(d.phone || ''))
      .catch(console.error);
  }, [token]);

  const save = async () => {
    setSaving(true);
    setMsg('');
    try {
      const res = await fetch(`${API_URL}/api/admin/alert-phone`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ phone }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Save failed');
      setPhone(d.phone || '');
      setMsg(d.phone ? `SMS alerts on — approvals & change requests text ${d.phone}` : 'SMS alerts off');
    } catch (err) {
      setMsg(String(err.message || err));
    } finally { setSaving(false); }
  };

  return (
    <div className="mb-4 p-3 rounded-lg bg-indigo-50/60 border border-indigo-100" data-testid="sms-alert-config">
      <div className="flex flex-wrap items-center gap-2">
        <Smartphone className="w-3.5 h-3.5 text-indigo-500" />
        <span className="text-[11px] font-bold text-gray-800">SMS alerts:</span>
        <input
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="+12145551234"
          className="w-40 px-2.5 py-1.5 border border-gray-200 rounded-lg text-xs text-gray-900 bg-white focus:outline-none focus:border-indigo-400"
          data-testid="sms-alert-phone-input"
        />
        <button
          onClick={save}
          disabled={saving}
          className="px-3 py-1.5 rounded-lg bg-indigo-500 hover:bg-indigo-600 text-white text-[10px] font-bold disabled:opacity-50 transition-colors"
          data-testid="sms-alert-phone-save"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        <span className="text-[10px] text-gray-500">Texts you the second an owner approves or requests changes. Blank = off.</span>
      </div>
      {msg && <p className="text-[10px] text-indigo-700 mt-1.5" data-testid="sms-alert-phone-msg">{msg}</p>}
    </div>
  );
}

export default function ListingApprovalLinksCard() {
  const [cities, setCities] = useState([]);
  const [city, setCity] = useState('');
  const [copied, setCopied] = useState('');

  useEffect(() => {
    fetch(`${API_URL}/api/admin/directory/preview-links`, {
      headers: { Authorization: `Bearer ${sessionStorage.getItem('admin_token')}` },
    })
      .then((r) => r.json())
      .then((d) => {
        setCities(d.cities || []);
        if (d.cities?.length) setCity(d.cities[0].city);
      })
      .catch(console.error);
  }, []);

  const selected = cities.find((c) => c.city === city);

  const copyLink = async (g) => {
    const url = `${window.location.origin}${g.preview_path}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      ta.remove();
    }
    setCopied(g.gym_id);
    setTimeout(() => setCopied(''), 2000);
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5" data-testid="listing-approval-links-card">
      <div className="flex items-center gap-2 mb-1">
        <Link2 className="w-4 h-4 text-teal-500" />
        <h2 className="text-sm font-bold text-gray-900">Listing Approval Links</h2>
      </div>
      <p className="text-xs text-gray-500 mb-4">
        Text a read-only preview link to a gym owner so they can sign off on their listing before it goes public. Approvals appear here and in the pipeline.
      </p>

      <SmsAlertConfig />

      <select
        value={city}
        onChange={(e) => setCity(e.target.value)}
        className="mb-3 px-3 py-2 rounded-lg border border-gray-200 text-xs font-semibold text-gray-700 bg-gray-50 focus:outline-none focus:border-teal-400"
        data-testid="approval-links-city-select"
      >
        {cities.map((c) => (
          <option key={c.city} value={c.city}>{c.label} ({c.gyms.length})</option>
        ))}
      </select>

      <div className="max-h-72 overflow-y-auto divide-y divide-gray-50" data-testid="approval-links-gym-list">
        {(selected?.gyms || []).map((g) => (
          <div key={g.gym_id} className="flex items-center justify-between gap-3 py-2" data-testid={`approval-link-row-${g.gym_id}`}>
            <div className="min-w-0">
              <p className="text-xs font-semibold text-gray-800 truncate">{g.name}</p>
              {g.approval?.approved ? (
                <p className="text-[10px] text-emerald-600 flex items-center gap-1" data-testid={`approval-status-${g.gym_id}`}>
                  <CheckCircle2 className="w-3 h-3" /> Approved by {g.approval.name} · {new Date(g.approval.approved_at).toLocaleDateString()}
                </p>
              ) : g.edit_request ? (
                <p className="text-[10px] text-amber-600 flex items-center gap-1" title={g.edit_request.changes} data-testid={`edit-request-status-${g.gym_id}`}>
                  <PencilLine className="w-3 h-3" /> Changes requested by {g.edit_request.name} · {new Date(g.edit_request.requested_at).toLocaleDateString()}
                </p>
              ) : (
                <p className="text-[10px] text-gray-400">Awaiting owner sign-off</p>
              )}
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <button
                onClick={() => copyLink(g)}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-gray-100 hover:bg-teal-50 hover:text-teal-700 text-gray-600 text-[10px] font-semibold transition-colors"
                data-testid={`copy-preview-link-${g.gym_id}`}
              >
                {copied === g.gym_id ? <><Check className="w-3 h-3 text-emerald-500" /> Copied</> : <><Copy className="w-3 h-3" /> Copy Link</>}
              </button>
              <a
                href={g.preview_path}
                target="_blank"
                rel="noopener noreferrer"
                className="p-1.5 rounded-lg text-gray-300 hover:text-teal-500 transition-colors"
                data-testid={`open-preview-link-${g.gym_id}`}
              >
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
